import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { BookUser, CreditCard, DollarSign, Globe, Loader2, Search } from 'lucide-react'
import { ApiClientError, apiClient } from '@/api/client'
import { useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import { mergePricingSettings } from '@/lib/counterPricing'
import { settleOnlyLabel } from '@/lib/houseAccounts'
import { marketplaceProviderSlugs, onlineProvidersForOrder } from '@/lib/marketplaceProviders'
import { toastHelpers } from '@/lib/toast-helpers'
import { cn, formatCurrency } from '@/lib/utils'
import type { HouseAccount, TabSettlementResult } from '@/types'

/**
 * F-MM-N30 — a guest paying down their tab.
 *
 * The money lands on TODAY's open business day and feeds the day's expected
 * cash / card / online (no drawer movement, so it is never counted twice).
 * The sale itself was filed on the day it was eaten; this is only the
 * collection. Oldest bills are paid first, and a bill billed at the card rate
 * can only be paid by card or online — so cash is capped at `cash_payable`.
 * A suspended or closed tab that still owes can be paid here, never charged.
 */

type Method = 'cash' | 'card' | 'online'

const methodMeta: Record<Method, { label: string; Icon: typeof DollarSign }> = {
  cash: { label: 'Cash', Icon: DollarSign },
  card: { label: 'Card', Icon: CreditCard },
  online: { label: 'Online', Icon: Globe },
}

/** What the slip needs beyond the server's result. */
export interface TabSettlementExtras {
  subMethodLabel: string | null
  referenceNumber: string | null
}

export interface ReceiveTabPaymentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with the server's result so the parent can print a slip. */
  onSettled?: (result: TabSettlementResult, account: HouseAccount, extras: TabSettlementExtras) => void
  /** Open straight on this account (customer profile → Receive payment). */
  preselectedAccountId?: string | null
}

const REFETCH_CODES = new Set(['settlement_exceeds_balance', 'tab_card_rate_requires_card'])

export function ReceiveTabPaymentDialog({
  open,
  onOpenChange,
  onSettled,
  preselectedAccountId,
}: ReceiveTabPaymentDialogProps) {
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [accountId, setAccountId] = useState<string | null>(null)
  const [method, setMethod] = useState<Method>('card')
  const [subMethod, setSubMethod] = useState('')
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')

  useEffect(() => {
    if (!open) return
    setQuery('')
    setDebounced('')
    setAccountId(preselectedAccountId ?? null)
    setMethod('card')
    setSubMethod('')
    setAmount('')
    setReference('')
  }, [open, preselectedAccountId])

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  const search = useQuery({
    queryKey: ['counter-house-accounts', 'tab', 'settle', debounced],
    queryFn: () => apiClient.searchCounterHouseAccounts({ q: debounced || undefined, kind: 'tab', purpose: 'settle' }),
    enabled: open && !accountId,
    staleTime: 15_000,
  })
  const accounts = (search.data?.data ?? []).filter((a) => a.balance > 0)

  const detail = useQuery({
    queryKey: ['counter-house-account', accountId],
    queryFn: () => apiClient.getCounterHouseAccount(accountId!),
    enabled: open && Boolean(accountId),
    retry: false,
  })
  const acc = detail.data?.data?.account
  const bills = detail.data?.data?.open_bills ?? []
  const cashPayable = detail.data?.data?.cash_payable ?? 0
  const balance = acc?.balance ?? 0
  const ceiling = method === 'cash' ? Math.min(cashPayable, balance) : balance

  // Online providers, the same list (and the same marketplace exclusion) the
  // checkout offers on an ordinary bill.
  const { data: pricingRaw } = useQuery({
    queryKey: ['counterPricing'],
    queryFn: () => apiClient.getCounterPricing().then((r) => (r.success && r.data ? r.data : null)),
    enabled: open,
  })
  const { raw: orderTypesRaw, marketplaceSettledEnabled } = useEnabledOrderTypes()
  const providers = useMemo(() => {
    const all = mergePricingSettings(pricingRaw).online_submethods ?? []
    const reserved = marketplaceProviderSlugs(orderTypesRaw, marketplaceSettledEnabled)
    return onlineProvidersForOrder(all, reserved, false)
  }, [pricingRaw, orderTypesRaw, marketplaceSettledEnabled])
  const needsProvider = method === 'online' && providers.length > 0
  const providerLabel = providers.find((p) => p.slug === subMethod)?.label ?? null

  // Default the amount to the whole balance the chosen method may settle.
  useEffect(() => {
    if (!acc) return
    setAmount(ceiling > 0 ? String(ceiling) : '')
  }, [acc, ceiling])

  const parsed = Number(amount.trim())
  const amountValid = amount.trim() !== '' && Number.isFinite(parsed) && parsed > 0
  const overCeiling = amountValid && Math.round(parsed * 100) > Math.round(ceiling * 100)
  const cardRateBills = useMemo(() => bills.some((b) => b.rate_intent === 'card'), [bills])

  const settle = useMutation({
    mutationFn: async () => {
      const res = await apiClient.recordTabSettlement(accountId!, {
        amount: parsed,
        method,
        payment_sub_method: method === 'online' && subMethod ? subMethod : undefined,
        reference_number: reference.trim() || undefined,
      })
      if (!res.success || !res.data) throw new Error(res.message || 'Could not record the payment')
      return res.data
    },
    onSuccess: (result) => {
      toastHelpers.success('Tab payment', `Received ${formatCurrency(result.amount)} from ${result.customer_name}`)
      queryClient.invalidateQueries({ queryKey: ['counter-house-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['counter-house-account'] })
      queryClient.invalidateQueries({ queryKey: ['house-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
      if (acc) {
        onSettled?.(result, acc, {
          subMethodLabel: method === 'online' ? providerLabel : null,
          referenceNumber: reference.trim() || null,
        })
      }
      onOpenChange(false)
    },
    onError: (err: Error) => {
      if (err instanceof ApiClientError && err.code && REFETCH_CODES.has(err.code)) {
        void detail.refetch()
      }
      toastHelpers.error('Tab payment', err.message)
    },
  })

  const canSubmit =
    Boolean(acc) && amountValid && !overCeiling && (!needsProvider || Boolean(subMethod)) && !settle.isPending
  const accLabel = acc ? settleOnlyLabel(acc.status) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* [&>*]:min-w-0 — DialogContent is a grid, and a grid item otherwise
          grows to its longest unbreakable line: a long guest name pushed the
          amounts and the confirm button out of the dialog. */}
      <DialogContent className="sm:max-w-md [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookUser className="h-4 w-4" aria-hidden />
            Receive tab payment
          </DialogTitle>
          <DialogDescription>Counted in today&apos;s takings. Oldest bills are paid first.</DialogDescription>
        </DialogHeader>

        {!accountId ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                autoFocus
                placeholder="Guest name or phone"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-10 pl-9"
              />
            </div>
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {search.isFetching && accounts.length === 0 ? (
                <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Looking up tabs…
                </div>
              ) : search.isError ? (
                <div className="space-y-2 py-3 text-sm">
                  <p className="text-destructive">{(search.error as Error)?.message || 'Could not look up tabs.'}</p>
                  <Button type="button" size="sm" variant="outline" onClick={() => void search.refetch()}>
                    Retry
                  </Button>
                </div>
              ) : accounts.length === 0 ? (
                <p className="py-3 text-sm text-muted-foreground">No tab with a balance matches.</p>
              ) : (
                accounts.map((a) => {
                  const label = settleOnlyLabel(a.status)
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setAccountId(a.id)}
                      className="flex w-full items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-muted/40"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium" title={a.customer_name}>
                          {a.customer_name}
                        </span>
                        {a.customer_phone ? (
                          <span className="block text-xs text-muted-foreground">{a.customer_phone}</span>
                        ) : null}
                        {label ? (
                          <Badge variant="outline" className="mt-1 text-[10px]">
                            {label}
                          </Badge>
                        ) : null}
                      </span>
                      <span className="shrink-0 font-medium tabular-nums">{formatCurrency(a.balance)}</span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        ) : detail.isError ? (
          <div className="space-y-3 py-4 text-sm">
            <p role="alert" className="text-destructive">
              {(detail.error as Error)?.message || 'Could not load this tab.'}
            </p>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => void detail.refetch()}>
                Retry
              </Button>
              {!preselectedAccountId ? (
                <Button type="button" size="sm" variant="ghost" onClick={() => setAccountId(null)}>
                  Change guest
                </Button>
              ) : null}
            </div>
          </div>
        ) : detail.isLoading || !acc ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading the tab…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-baseline justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium" title={acc.customer_name}>
                  {acc.customer_name}
                </div>
                {accLabel ? (
                  <Badge variant="outline" className="mt-1 text-[10px]">
                    {accLabel}
                  </Badge>
                ) : null}
                {!preselectedAccountId ? (
                  <button type="button" className="block text-xs text-primary underline" onClick={() => setAccountId(null)}>
                    Change guest
                  </button>
                ) : null}
              </div>
              <div className="shrink-0 text-right">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Owes</div>
                <div className="text-lg font-semibold tabular-nums">{formatCurrency(balance)}</div>
              </div>
            </div>

            {bills.length > 0 ? (
              <div className="max-h-36 space-y-1 overflow-y-auto text-xs">
                {bills.map((b) => (
                  <div key={b.order_id} className="flex justify-between gap-2 text-muted-foreground">
                    <span className="truncate">
                      #{b.order_number} · {new Date(b.charged_at).toLocaleDateString()}
                      {b.rate_intent === 'card' ? ' · card rate' : ''}
                    </span>
                    <span className="tabular-nums">{formatCurrency(b.outstanding)}</span>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="grid grid-cols-3 gap-1.5">
              {(['cash', 'card', 'online'] as const).map((m) => {
                const { label, Icon } = methodMeta[m]
                const disabled = m === 'cash' && cashPayable <= 0
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setMethod(m)
                      if (m !== 'online') setSubMethod('')
                    }}
                    title={disabled ? 'These bills were billed at the card rate — they can only be paid by card or online.' : undefined}
                    className={cn(
                      'flex h-11 flex-col items-center justify-center rounded-lg border text-[11px] font-semibold uppercase tracking-wide transition-colors',
                      method === m ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted/40',
                      disabled && 'cursor-not-allowed opacity-40',
                    )}
                    aria-pressed={method === m}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    {label}
                  </button>
                )
              })}
            </div>
            {cardRateBills ? (
              <p className="text-[11px] text-muted-foreground">
                {cashPayable > 0
                  ? `Cash can settle up to ${formatCurrency(cashPayable)} — the rest was billed at the card rate and must be paid by card or online.`
                  : 'Billed at the card rate — pay by card or online.'}
              </p>
            ) : null}

            {needsProvider ? (
              <div className="space-y-1.5">
                <span className="text-sm font-medium">Paid through</span>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {providers.map((p) => (
                    <button
                      key={p.slug}
                      type="button"
                      onClick={() => setSubMethod(p.slug)}
                      aria-pressed={subMethod === p.slug}
                      className={cn(
                        'h-10 rounded-lg border px-2 text-sm font-medium transition-colors',
                        subMethod === p.slug
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border hover:bg-muted/40',
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <label htmlFor="tab-settle-amount" className="text-sm font-medium">
                Amount
              </label>
              <Input
                id="tab-settle-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-11 text-base tabular-nums"
              />
              {overCeiling ? (
                <p role="alert" className="text-sm font-medium text-destructive">
                  At most {formatCurrency(ceiling)} by {methodMeta[method].label.toLowerCase()}.
                </p>
              ) : null}
            </div>
            {method !== 'cash' ? (
              <Input
                placeholder="Reference (optional)"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="h-9 text-sm"
              />
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={() => settle.mutate()}>
            {settle.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
            {amountValid ? `Receive ${formatCurrency(parsed)}` : 'Receive'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
